import React, {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from 'react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { useMnLayer } from '../hooks/useMnLayer';
import { useUnits } from '../hooks/useUnits';
import type { UseUnits, UnitSystem } from '../hooks/useUnits';
import type { Layer, Load, AnalysisPoint, ResultPoint } from '../hooks/mnlayer.types';

// ── Types ──────────────────────────────────────────────────────────────────────

type PlotType     = 'XZ' | 'YZ' | 'XY';
type PlotMode     = 'curves' | 'blocks' | 'canvas';
type AnalysisMode = 'plot' | 'points';

type ResultKey =
  | 'stress_xx' | 'stress_yy' | 'stress_zz'
  | 'stress_xy' | 'stress_yz' | 'stress_zx'
  | 'strain_xx' | 'strain_yy' | 'strain_zz'
  | 'horizontal_displacement_x' | 'horizontal_displacement_y'
  | 'vertical_displacement'
  | 'sigma1' | 'sigma2' | 'sigma3';

type EnrichedPoint = ResultPoint & {
  _reqZ:  number;
  sigma1: number;
  sigma2: number;
  sigma3: number;
};

interface LayerInterface { z: number; topLayer: number; bottomLayer: number; }

interface GridConfig {
  plotType: PlotType;
  axis1Min: number; axis1Max: number;
  axis2Min: number; axis2Max: number;
  xzFixedY: number;
  yzFixedX: number;
  xyXMin: number; xyXMax: number;
  xyYMin: number; xyYMax: number;
  xyLayer: number; xyZ: number;
}

// ── Constants ──────────────────────────────────────────────────────────────────

const INTF_OFFSET = 0.001;

const PROPERTIES: { value: ResultKey; label: string; group?: string }[] = [
  { value: 'stress_xx',                 label: 'σ_xx  — Stress XX'             },
  { value: 'stress_yy',                 label: 'σ_yy  — Stress YY'             },
  { value: 'stress_zz',                 label: 'σ_zz  — Stress ZZ'             },
  { value: 'stress_xy',                 label: 'τ_xy  — Shear XY'              },
  { value: 'stress_yz',                 label: 'τ_yz  — Shear YZ'              },
  { value: 'stress_zx',                 label: 'τ_zx  — Shear ZX'              },
  { value: 'strain_xx',                 label: 'ε_xx  — Strain XX'             },
  { value: 'strain_yy',                 label: 'ε_yy  — Strain YY'             },
  { value: 'strain_zz',                 label: 'ε_zz  — Strain ZZ'             },
  { value: 'vertical_displacement',     label: 'u_z   — Vertical displ.'       },
  { value: 'horizontal_displacement_x', label: 'u_x  — Horiz. displ. X'       },
  { value: 'horizontal_displacement_y', label: 'u_y  — Horiz. displ. Y'       },
  { value: 'sigma1', label: 'σ₁  — Major principal',        group: 'Principal Stresses' },
  { value: 'sigma2', label: 'σ₂  — Intermediate principal', group: 'Principal Stresses' },
  { value: 'sigma3', label: 'σ₃  — Minor principal',        group: 'Principal Stresses' },
];

const DEFAULT_LAYERS: Layer[] = [
  { elastic_modulus: 4000, poisson_ratio: 0.15, thickness: 5.2, bond_coefficient: 0 },
  { elastic_modulus: 4000, poisson_ratio: 0.15, thickness: null, bond_coefficient: null },
];
const DEFAULT_LOADS: Load[] = [
  { pressure: 100, radius: 6, x_coordinate: 0, y_coordinate: 0 },
];
const DEFAULT_POINTS: AnalysisPoint[] = [
  { layer_number: 1, x_coordinate: 0, y_coordinate: 0, z_coordinate: 5.2 },
];
const DEFAULT_GRID: GridConfig = {
  plotType: 'XZ',
  axis1Min: -10, axis1Max: 10,
  axis2Min: 0,   axis2Max: 10,
  xzFixedY: 0,   yzFixedX: 0,
  xyXMin: 0, xyXMax: 10,
  xyYMin: 0, xyYMax: 10,
  xyLayer: 1, xyZ: 0,
};

const MODE_LABELS: Record<PlotMode, string> = { curves: 'Contour', blocks: 'Blocks', canvas: 'Canvas' };
const MODE_CYCLE: PlotMode[] = ['curves', 'blocks', 'canvas'];

const VIRIDIS: [number, [number, number, number]][] = [
  [0.0,[68,1,84]],    [0.1,[72,35,116]],   [0.2,[64,67,135]],
  [0.3,[52,94,141]],  [0.4,[33,144,140]],  [0.5,[39,173,129]],
  [0.6,[92,200,99]],  [0.7,[170,220,50]],  [0.8,[224,236,29]],
  [0.9,[253,231,37]], [1.0,[253,231,37]],
];

// ── Shared styles ──────────────────────────────────────────────────────────────

const inputCls      = 'w-full rounded border border-gray-200 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#003594]/30 focus:border-[#003594] transition-colors';
const inputErrCls   = 'w-full rounded border border-red-400 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-300 focus:border-red-500 transition-colors bg-red-50/30';
const inputComputedCls = 'w-full rounded border border-[#003594]/25 bg-[#003594]/5 px-2 py-1.5 text-sm text-[#003594] font-medium cursor-default';
const inputSmCls = 'rounded border border-gray-200 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#003594]/30 focus:border-[#003594] transition-colors';
const selectCls  = 'rounded border border-gray-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#003594]/30 focus:border-[#003594] bg-white';
const thCls      = 'px-3 py-2.5 text-left text-sm font-semibold text-gray-500 tracking-wide bg-gray-50';
const tdCls      = 'px-3 py-2';
const numTd      = 'px-3 py-2 text-sm text-gray-400 font-mono w-8';
const monoFont   = { fontFamily: "'DM Mono', monospace" };
const serifFont  = { fontFamily: "'Crimson Pro', Georgia, serif" };

// ── Pure helpers ───────────────────────────────────────────────────────────────

function computePrincipals(r: ResultPoint) {
  const sxx = r.stress_xx ?? 0, syy = r.stress_yy ?? 0, szz = r.stress_zz ?? 0;
  const szx = r.stress_zx ?? 0;
  const avg = (sxx + szz) / 2;
  const R   = Math.sqrt(((sxx - szz) / 2) ** 2 + szx ** 2);
  const all = [syy, avg + R, avg - R].sort((a, b) => b - a);
  return { sigma1: all[0], sigma2: all[1], sigma3: all[2] };
}

function getLayerInterfaces(layers: Layer[]): LayerInterface[] {
  const interfaces: LayerInterface[] = [];
  let cumZ = 0;
  for (let i = 0; i < layers.length - 1; i++) {
    const t = Number(layers[i].thickness) || 0;
    cumZ += t;
    interfaces.push({ z: cumZ, topLayer: i + 1, bottomLayer: i + 2 });
  }
  return interfaces;
}

function layerForZ(z: number, interfaces: LayerInterface[]): number {
  for (let k = 0; k < interfaces.length; k++) {
    if (z <= interfaces[k].z) return k + 1;
  }
  return interfaces.length + 1;
}

function generateGridPoints(layers: Layer[], grid: GridConfig): AnalysisPoint[] {
  const points: AnalysisPoint[] = [];
  const interfaces = getLayerInterfaces(layers);
  const { plotType } = grid;

  if (plotType === 'XZ' || plotType === 'YZ') {
    const { axis1Min, axis1Max, axis2Min, axis2Max } = grid;
    const axis1Step  = (axis1Max - axis1Min) / 11;
    const axis2Step  = (axis2Max - axis2Min) / 11;
    const fixedCoord = plotType === 'XZ' ? grid.xzFixedY : grid.yzFixedX;

    const pushPoint = (axis1Val: number, zGlobal: number, layerNum: number) => {
      points.push({
        layer_number: layerNum,
        x_coordinate: plotType === 'XZ' ? parseFloat(axis1Val.toFixed(4)) : parseFloat(fixedCoord.toFixed(4)),
        y_coordinate: plotType === 'YZ' ? parseFloat(axis1Val.toFixed(4)) : parseFloat(fixedCoord.toFixed(4)),
        z_coordinate: parseFloat(zGlobal.toFixed(4)),
      });
    };

    for (let i = 0; i < 12; i++) {
      const axis1Val = axis1Min + i * axis1Step;
      for (let j = 0; j < 12; j++) {
        const zVal = axis2Min + j * axis2Step;
        let nearInterface = false;
        for (const intf of interfaces) {
          if (Math.abs(zVal - intf.z) < 0.01) { nearInterface = true; break; }
        }
        if (nearInterface) continue;
        pushPoint(axis1Val, zVal, layerForZ(zVal, interfaces));
      }
    }
    for (const intf of interfaces) {
      if (intf.z < axis2Min - 0.01 || intf.z > axis2Max + 0.01) continue;
      for (let i = 0; i < 12; i++) {
        const axis1Val = axis1Min + i * axis1Step;
        pushPoint(axis1Val, intf.z, intf.topLayer);
        pushPoint(axis1Val, intf.z + INTF_OFFSET, intf.bottomLayer);
      }
    }
  } else {
    const { xyXMin, xyXMax, xyYMin, xyYMax, xyLayer, xyZ } = grid;
    const xStep = (xyXMax - xyXMin) / 11, yStep = (xyYMax - xyYMin) / 11;
    for (let i = 0; i < 12; i++) {
      for (let j = 0; j < 12; j++) {
        points.push({
          layer_number: xyLayer,
          x_coordinate: parseFloat((xyXMin + i * xStep).toFixed(4)),
          y_coordinate: parseFloat((xyYMin + j * yStep).toFixed(4)),
          z_coordinate: parseFloat(xyZ.toFixed(4)),
        });
      }
    }
  }
  return points;
}

function colorFor(val: number | null, vMin: number, vMax: number): string {
  if (val == null || !isFinite(val)) return 'rgba(200,200,200,0.3)';
  let t = Math.max(0, Math.min(1, (val - vMin) / (vMax - vMin)));
  let i = 0;
  for (; i < VIRIDIS.length - 1; i++) { if (t <= VIRIDIS[i + 1][0]) break; }
  const [t0, c0] = VIRIDIS[i];
  const [t1, c1] = VIRIDIS[Math.min(i + 1, VIRIDIS.length - 1)];
  const f = t1 > t0 ? (t - t0) / (t1 - t0) : 0;
  return `rgb(${Math.round(c0[0]+f*(c1[0]-c0[0]))},${Math.round(c0[1]+f*(c1[1]-c0[1]))},${Math.round(c0[2]+f*(c1[2]-c0[2]))})`;
}

function formatPropertyName(key: ResultKey): string {
  return key.replace(/_([a-z]+)/g, (_m, p1) => ' ' + p1.toUpperCase())
            .replace(/\b\w/g, l => l.toUpperCase());
}

const fmtSci   = (n: number) => Number(n).toExponential(4);
const fmtCoord = (n: number) => Number(n).toFixed(2);

// Plain-decimal formatter that never falls back to exponent notation, keeping
// ~5 significant figures. Small values (strains) get more decimal places so
// their precision survives; trailing zeros are stripped.
const fmtDecimal = (n: number, sig = 5): string => {
  if (!isFinite(n)) return '';
  if (n === 0) return '0';
  const exp = Math.floor(Math.log10(Math.abs(n)));
  const decimals = Math.min(12, Math.max(0, sig - 1 - exp));
  let s = n.toFixed(decimals);
  if (s.indexOf('.') >= 0) s = s.replace(/0+$/, '').replace(/\.$/, '');
  return s;
};

type ResultNotation = 'scientific' | 'decimal';

// Displayed values use a consistent number of significant figures (not fixed
// decimals), so small quantities keep their precision while large ones don't
// grow long decimal tails. The stored value is always full precision and is
// what every calculation uses, so this only affects on-screen rounding.
// Bump this to 5 or 6 if more displayed digits are wanted.
const DISPLAY_SIG_FIGS = 4;

// Clean numeric string: DISPLAY_SIG_FIGS significant figures, trailing zeros
// stripped. Keeps converted values readable (50.31, not 50.30728...) without
// forcing decimals on whole numbers (4000).
function formatNum(n: number | null): string {
  if (n == null || !isFinite(n)) return '';
  if (n === 0) return '0';
  return Number(n.toPrecision(DISPLAY_SIG_FIGS)).toString();
}

// Map a result property to the unit dimension it should be displayed in.
// Strains are dimensionless (null -> shown unchanged in every system).
function dimForResultKey(key: ResultKey): string | null {
  if (key.startsWith('strain_')) return null;
  if (key.startsWith('stress_') || key === 'sigma1' || key === 'sigma2' || key === 'sigma3')
    return 'pressure';
  return 'length'; // horizontal_displacement_x/y, vertical_displacement
}

// ── Validation helpers ─────────────────────────────────────────────────────

interface LayerError  { elastic_modulus?: string; poisson_ratio?: string; thickness?: string; bond_coefficient?: string; }
interface PointError  { layer_number?: string; z_coordinate?: string; }

function validateLayers(layers: Layer[]): LayerError[] {
  return layers.map((l, idx) => {
    const errs: LayerError = {};
    const isLast = idx === layers.length - 1;
    const e = Number(l.elastic_modulus);
    const pr = Number(l.poisson_ratio);
    const th = Number(l.thickness);
    const bc = Number(l.bond_coefficient);
    if (l.elastic_modulus === null || isNaN(e) || e <= 0)
      errs.elastic_modulus = 'E must be > 0';
    if (l.poisson_ratio === null || isNaN(pr) || pr < 0.1 || pr > 0.499)
      errs.poisson_ratio = 'ν must be 0.10 – 0.499';
    if (!isLast) {
      if (l.thickness === null || isNaN(th) || th <= 0)
        errs.thickness = 'Thickness must be > 0';
      if (l.bond_coefficient === null || isNaN(bc) || bc < 0 || bc > 1000)
        errs.bond_coefficient = 'Bond must be 0 – 1000';
    }
    return errs;
  });
}

// layerTops: cumulative depth to the top of each layer (index = layer number - 1)
function getLayerTops(layers: Layer[]): number[] {
  const tops: number[] = [0];
  for (let i = 0; i < layers.length - 1; i++) {
    tops.push(tops[i] + (Number(layers[i].thickness) || 0));
  }
  return tops;
}

function validatePoints(points: AnalysisPoint[], layers: Layer[], units?: UseUnits): PointError[] {
  const nLayers = layers.length;
  return points.map(pt => {
    const errs: PointError = {};
    const ln  = Number(pt.layer_number);
    const z   = Number(pt.z_coordinate);
    if (pt.layer_number === null || isNaN(ln) || ln < 1 || ln > nLayers || !Number.isInteger(ln))
      errs.layer_number = `Layer must be 1 – ${nLayers}`;
    if (pt.z_coordinate === null || isNaN(z) || z < 0)
      errs.z_coordinate = 'Z must be ≥ 0';
    else if (!isNaN(ln) && ln >= 1 && ln <= nLayers) {
      const isLast = ln === nLayers;
      if (!isLast) {
        // Validation is done entirely in stored (US) units; only the displayed
        // thickness in the message is converted to the current system.
        const th = Number(layers[ln - 1].thickness);
        if (!isNaN(th) && th > 0 && z > th) {
          const shownTh  = units ? formatNum(units.toDisplay('length', th)) : String(th);
          const shownSym = units ? units.symbol('length') : 'in.';
          errs.z_coordinate = `Z exceeds layer ${ln} thickness (${shownTh} ${shownSym})`;
        }
      }
    }
    return errs;
  });
}

function hasErrors(errs: object[]): boolean {
  return errs.some(e => Object.keys(e).length > 0);
}

// Collect all unique error messages from a list of error objects
function errorMessages(errs: object[]): string[] {
  const seen = new Set<string>();
  const msgs: string[] = [];
  errs.forEach(e => Object.values(e).forEach((v: any) => {
    if (v && !seen.has(v)) { seen.add(v); msgs.push(v); }
  }));
  return msgs;
}

// ── Canvas renderer ────────────────────────────────────────────────────────────

function renderCanvas(
  container: HTMLDivElement,
  results: EnrichedPoint[],
  xAxis: 'x'|'y'|'z',
  yAxis: 'x'|'y'|'z',
  property: ResultKey,
  layers: Layer[],
  colorScale: 'global' | 'per-layer' = 'global',
  coordUnit = '',
  valueUnit = '',
) {
  container.innerHTML = '';
  const WIDTH = container.clientWidth || 900, HEIGHT = 600;
  const M = { top: 50, right: 80, bottom: 50, left: 60 };
  const plotW = WIDTH - M.left - M.right, plotH = HEIGHT - M.top - M.bottom;
  const yReversed = yAxis === 'z';
  const ck = (a: number, b: number) => `${a}::${b}`;

  const xCoords = [...new Set(results.map(r => r[xAxis] as number))].sort((a, b) => a - b);
  const yCoords = [...new Set(results.map(r => r[yAxis] as number))].sort((a, b) => a - b);
  const valMap  = new Map<string, number>();
  results.forEach(r => valMap.set(ck(r[xAxis] as number, r[yAxis] as number), r[property] as number));

  let vMin = Infinity, vMax = -Infinity;
  results.forEach(r => {
    const v = r[property] as number;
    if (v != null && isFinite(v)) { vMin = Math.min(vMin, v); vMax = Math.max(vMax, v); }
  });
  if (vMin >= vMax) { vMin -= 1; vMax += 1; }

  const interfaces = getLayerInterfaces(layers);
  const interfaceZs = interfaces.map(i => i.z);
  const segments: { startIdx: number; endIdx: number }[] = [];
  let segStart = 0;
  for (let i = 1; i < yCoords.length; i++) {
    const y0 = yCoords[i-1], y1 = yCoords[i];
    let hit = false;
    for (const iz of interfaceZs) {
      if (iz >= y0-0.002 && iz <= y1+0.002 && Math.abs(y1-y0) < 0.01) { hit = true; break; }
    }
    if (hit) { segments.push({ startIdx: segStart, endIdx: i-1 }); segStart = i; }
  }
  segments.push({ startIdx: segStart, endIdx: yCoords.length - 1 });

  const xMin = xCoords[0], xMax = xCoords[xCoords.length-1];
  const yMin = yCoords[0], yMax = yCoords[yCoords.length-1];
  const xScale = (v: number) => M.left + (v-xMin)/(xMax-xMin)*plotW;
  const yScale = (v: number) => {
    const t = (v-yMin)/(yMax-yMin);
    return yReversed ? M.top+t*plotH : M.top+(1-t)*plotH;
  };
  const bilinear = (px: number, py: number, xArr: number[], yArr: number[]): number|null => {
    let xi=0; for(;xi<xArr.length-2;xi++){if(xArr[xi+1]>=px)break;}
    let yi=0; for(;yi<yArr.length-2;yi++){if(yArr[yi+1]>=py)break;}
    const x0=xArr[xi],x1=xArr[xi+1],y0=yArr[yi],y1=yArr[yi+1];
    const v00=valMap.get(ck(x0,y0))??null,v10=valMap.get(ck(x1,y0))??null;
    const v01=valMap.get(ck(x0,y1))??null,v11=valMap.get(ck(x1,y1))??null;
    if(v00==null||v10==null||v01==null||v11==null)return null;
    const tx=x1>x0?(px-x0)/(x1-x0):0, ty=y1>y0?(py-y0)/(y1-y0):0;
    return v00*(1-tx)*(1-ty)+v10*tx*(1-ty)+v01*(1-tx)*ty+v11*tx*ty;
  };

  // Per-segment color ranges (used in per-layer mode)
  const segmentRanges: { vMin: number; vMax: number }[] = segments.map(seg => {
    const segY = yCoords.slice(seg.startIdx, seg.endIdx + 1);
    let sMin = Infinity, sMax = -Infinity;
    results.forEach(r => {
      const ry = r[yAxis] as number;
      if (segY.includes(ry)) {
        const v = r[property] as number;
        if (v != null && isFinite(v)) { sMin = Math.min(sMin, v); sMax = Math.max(sMax, v); }
      }
    });
    if (sMin >= sMax) { sMin -= 1; sMax += 1; }
    return { vMin: sMin, vMax: sMax };
  });

  const canvas = document.createElement('canvas');
  canvas.width=WIDTH; canvas.height=HEIGHT;
  canvas.style.width='100%'; canvas.style.height=HEIGHT+'px';
  container.appendChild(canvas);
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle='#fff'; ctx.fillRect(0,0,WIDTH,HEIGHT);

  for (let si = 0; si < segments.length; si++) {
    const seg = segments[si];
    const segY = yCoords.slice(seg.startIdx, seg.endIdx+1);
    if (segY.length < 2) continue;
    const segVMin = colorScale === 'per-layer' ? segmentRanges[si].vMin : vMin;
    const segVMax = colorScale === 'per-layer' ? segmentRanges[si].vMax : vMax;
    const py0 = Math.round(yScale(segY[0])), py1 = Math.round(yScale(segY[segY.length-1]));
    const pxL = Math.round(xScale(xMin)), pxR = Math.round(xScale(xMax));
    const pyT = Math.min(py0,py1), pyB = Math.max(py0,py1);
    for (let px=pxL; px<=pxR; px++) {
      for (let py=pyT; py<=pyB; py++) {
        const dataX = xMin+(px-M.left)/plotW*(xMax-xMin);
        const dataY = yReversed ? yMin+(py-M.top)/plotH*(yMax-yMin) : yMax-(py-M.top)/plotH*(yMax-yMin);
        const val = bilinear(dataX, dataY, xCoords, segY);
        if (val!=null) { ctx.fillStyle=colorFor(val,segVMin,segVMax); ctx.fillRect(px,py,1,1); }
      }
    }
  }

  ctx.strokeStyle='rgba(255,255,255,0.6)'; ctx.lineWidth=1.5; ctx.setLineDash([4,3]);
  for (const iz of interfaceZs) {
    if (iz>=yMin && iz<=yMax) {
      const py=Math.round(yScale(iz));
      ctx.beginPath(); ctx.moveTo(M.left,py); ctx.lineTo(M.left+plotW,py); ctx.stroke();
    }
  }
  ctx.setLineDash([]);
  ctx.strokeStyle='#334155'; ctx.lineWidth=1;
  ctx.beginPath(); ctx.moveTo(M.left,M.top); ctx.lineTo(M.left,M.top+plotH);
  ctx.lineTo(M.left+plotW,M.top+plotH); ctx.stroke();
  ctx.fillStyle='#334155'; ctx.font='12px sans-serif'; ctx.textAlign='center';
  for (let i=0;i<=6;i++){
    const v=xMin+(xMax-xMin)*i/6, px=xScale(v);
    ctx.beginPath(); ctx.moveTo(px,M.top+plotH); ctx.lineTo(px,M.top+plotH+5); ctx.stroke();
    ctx.fillText(v.toFixed(1),px,M.top+plotH+18);
  }
  ctx.textAlign='right';
  for (let i=0;i<=6;i++){
    const v=yMin+(yMax-yMin)*i/6, py=yScale(v);
    ctx.beginPath(); ctx.moveTo(M.left-5,py); ctx.lineTo(M.left,py); ctx.stroke();
    ctx.fillText(v.toFixed(1),M.left-8,py+4);
  }
  ctx.textAlign='center'; ctx.font='13px sans-serif';
  const coordSuffix = coordUnit ? ` (${coordUnit})` : '';
  const valueSuffix = valueUnit ? ` (${valueUnit})` : '';
  ctx.fillText(`${xAxis.toUpperCase()} Coordinate${coordSuffix}`,M.left+plotW/2,HEIGHT-8);
  ctx.save(); ctx.translate(15,M.top+plotH/2); ctx.rotate(-Math.PI/2);
  ctx.fillText(`${yAxis.toUpperCase()} Coordinate${coordSuffix}`,0,0); ctx.restore();
  ctx.font='bold 15px sans-serif';
  ctx.fillText(`${formatPropertyName(property)}${valueSuffix} Distribution`,M.left+plotW/2,25);
  const cbX=WIDTH-M.right+15, cbW=15, cbH=plotH;
  if (colorScale === 'global') {
    for (let i=0;i<cbH;i++){
      const t=yReversed?i/cbH:1-i/cbH;
      ctx.fillStyle=colorFor(vMin+t*(vMax-vMin),vMin,vMax); ctx.fillRect(cbX,M.top+i,cbW,1);
    }
    ctx.strokeStyle='#334155'; ctx.strokeRect(cbX,M.top,cbW,cbH);
    ctx.font='10px sans-serif'; ctx.textAlign='left';
    for (let i=0;i<=5;i++){
      const t=i/5, v=yReversed?vMin+t*(vMax-vMin):vMax-t*(vMax-vMin);
      ctx.fillStyle='#334155'; ctx.fillText(v.toFixed(2),cbX+cbW+4,M.top+t*cbH+3);
    }
  } else {
    // Per-layer: draw mini colorbar per segment
    for (let si = 0; si < segments.length; si++) {
      const seg = segments[si];
      const segY = yCoords.slice(seg.startIdx, seg.endIdx + 1);
      if (segY.length < 2) continue;
      const { vMin: sMin, vMax: sMax } = segmentRanges[si];
      const py0 = Math.round(yScale(segY[0])), py1 = Math.round(yScale(segY[segY.length - 1]));
      const bandT = Math.min(py0, py1), bandB = Math.max(py0, py1);
      const bandH = bandB - bandT;
      for (let i = 0; i < bandH; i++) {
        const t = yReversed ? i / bandH : 1 - i / bandH;
        ctx.fillStyle = colorFor(sMin + t * (sMax - sMin), sMin, sMax);
        ctx.fillRect(cbX, bandT + i, cbW, 1);
      }
      ctx.strokeStyle = '#334155'; ctx.lineWidth = 0.5;
      ctx.strokeRect(cbX, bandT, cbW, bandH);
      ctx.font = '9px sans-serif'; ctx.textAlign = 'left'; ctx.fillStyle = '#334155';
      ctx.fillText(sMax.toExponential(1), cbX + cbW + 3, bandT + 9);
      ctx.fillText(sMin.toExponential(1), cbX + cbW + 3, bandB - 2);
    }
    // Label
    ctx.save(); ctx.translate(WIDTH - 6, M.top + plotH / 2); ctx.rotate(-Math.PI / 2);
    ctx.font = '9px sans-serif'; ctx.textAlign = 'center'; ctx.fillStyle = '#666';
    ctx.fillText('per-layer scale', 0, 0); ctx.restore();
  }
}

// ── Plotly renderer ────────────────────────────────────────────────────────────

function renderPlotly(
  containerId: string,
  results: EnrichedPoint[],
  xAxis: 'x'|'y'|'z',
  yAxis: 'x'|'y'|'z',
  property: ResultKey,
  mode: 'curves'|'blocks',
  layers: Layer[],
  colorScale: 'global' | 'per-layer' = 'global',
  coordUnit = '',
  valueUnit = '',
) {
  const coordSuffix = coordUnit ? ` (${coordUnit})` : '';
  const valueSuffix = valueUnit ? ` (${valueUnit})` : '';
  const Plotly = (window as any).Plotly;
  if (!Plotly) return;

  const ck = (a: number, b: number) => `${a}::${b}`;
  const xCoords = [...new Set(results.map(r => r[xAxis] as number))].sort((a, b) => a-b);
  const yCoords = [...new Set(results.map(r => r[yAxis] as number))].sort((a, b) => a-b);
  const dataMap = new Map<string, number>();
  results.forEach(r => dataMap.set(ck(r[xAxis] as number, r[yAxis] as number), r[property] as number));

  const interfaces = getLayerInterfaces(layers);
  const interfaceZs = interfaces.map(i => i.z);

  // Split yCoords into per-layer segments (same logic as canvas)
  const segments: { startIdx: number; endIdx: number }[] = [];
  let segStart = 0;
  for (let i = 1; i < yCoords.length; i++) {
    const y0 = yCoords[i-1], y1 = yCoords[i];
    let hit = false;
    if (yAxis === 'z') {
      for (const iz of interfaceZs) {
        if (iz >= y0-0.002 && iz <= y1+0.002 && Math.abs(y1-y0) < 0.01) { hit = true; break; }
      }
    }
    if (hit) { segments.push({ startIdx: segStart, endIdx: i-1 }); segStart = i; }
  }
  segments.push({ startIdx: segStart, endIdx: yCoords.length - 1 });

  const shapes: any[] = [], annotations: any[] = [];
  annotations.push({
    text: 'Khazanovich & Wang (2007) · MnLayer',
    xref: 'paper', yref: 'paper',
    x: 1, y: 0,
    xanchor: 'right', yanchor: 'bottom',
    showarrow: false,
    font: { size: 8.5, color: 'rgba(0, 0, 0, 0.55)', family: "'DM Mono', monospace" },
  });
  if (yAxis === 'z') {
    const xMin=Math.min(...xCoords), xMax=Math.max(...xCoords);
    const yMin=Math.min(...yCoords), yMax=Math.max(...yCoords);
    interfaces.filter(i => i.z>=yMin-0.01 && i.z<=yMax+0.01).forEach(intf => {
      shapes.push({ type:'line', x0:xMin, x1:xMax, y0:intf.z, y1:intf.z,
        line:{ color:'rgba(255,255,255,0.55)', width:1.5, dash:'dot' }, xref:'x', yref:'y' });
      annotations.push({ x:xMax, y:intf.z, text:`L${intf.topLayer}|L${intf.bottomLayer}`,
        xanchor:'right', yanchor:'bottom', showarrow:false,
        font:{ size:9, color:'rgba(255,255,255,0.55)' }, xref:'x', yref:'y' });
    });
  }

  const cfg = { responsive:true, displayModeBar:false };

  if (colorScale === 'global' || segments.length <= 1) {
    // ── Global scale: single trace, single coloraxis ──────────────────────────
    const zMatrix = yCoords.map(yc => xCoords.map(xc => dataMap.get(ck(xc,yc)) ?? null));
    const layout: any = {
      title: { text:'', pad:{ t:0 } },
      margin: { l:60, r:20, t:20, b:55 },
      xaxis: {
        title:{ text:`${xAxis.toUpperCase()} coordinate${coordSuffix}`, font:{ size:12 }, standoff:8 },
        showgrid:true, gridcolor:'rgba(0,0,0,0.06)', tickfont:{ size:10 }, fixedrange: true,
      },
      yaxis: {
        title:{ text:`${yAxis.toUpperCase()} coordinate${coordSuffix}`, font:{ size:12 }, standoff:8 },
        autorange: yAxis==='z' ? 'reversed' : true,
        showgrid:true, gridcolor:'rgba(0,0,0,0.06)', tickfont:{ size:10 }, fixedrange: true,
      },
      coloraxis: {
        colorscale:'Viridis',
        colorbar:{ title:{ text:`${formatPropertyName(property)}${valueSuffix}`, font:{ size:11 }, side:'right' }, thickness:14, tickfont:{ size:10 } },
      },
      shapes, annotations,
    };
    const trace: any = mode === 'blocks'
      ? { type:'heatmap', x:xCoords, y:yCoords, z:zMatrix, coloraxis:'coloraxis', zsmooth:false,
          hovertemplate:`${xAxis}: %{x}<br>${yAxis}: %{y}<br>${formatPropertyName(property)}: %{z:.4f}<extra></extra>` }
      : { type:'contour', x:xCoords, y:yCoords, z:zMatrix, coloraxis:'coloraxis',
          contours:{ showlines:true, coloring:'heatmap', showlabels:true, labelfont:{ size:9, color:'white' } },
          line:{ width:0.5, color:'rgba(255,255,255,0.25)' },
          hovertemplate:`${xAxis}: %{x}<br>${yAxis}: %{y}<br>${formatPropertyName(property)}: %{z:.4f}<extra></extra>` };
    Plotly.newPlot(containerId, [trace], layout, cfg);
  } else {
    // ── Per-layer scale: one trace + one coloraxis per layer segment ──────────
    const coloraxisKeys = segments.map((_, i) => i === 0 ? 'coloraxis' : `coloraxis${i + 1}`);
    const traces: any[] = segments.map((seg, si) => {
      const segYCoords = yCoords.slice(seg.startIdx, seg.endIdx + 1);
      const segZ = segYCoords.map(yc => xCoords.map(xc => dataMap.get(ck(xc, yc)) ?? null));
      let sMin = Infinity, sMax = -Infinity;
      segZ.forEach(row => row.forEach(v => { if (v != null && isFinite(v)) { sMin = Math.min(sMin, v); sMax = Math.max(sMax, v); } }));
      if (sMin >= sMax) { sMin -= 1; sMax += 1; }
      const caxisKey = coloraxisKeys[si];
      return mode === 'blocks'
        ? { type:'heatmap', x:xCoords, y:segYCoords, z:segZ,
            coloraxis: caxisKey, zsmooth:false,
            zmin: sMin, zmax: sMax,
            hovertemplate:`${xAxis}: %{x}<br>${yAxis}: %{y}<br>${formatPropertyName(property)}: %{z:.4f}<extra></extra>` }
        : { type:'contour', x:xCoords, y:segYCoords, z:segZ,
            coloraxis: caxisKey,
            zmin: sMin, zmax: sMax,
            contours:{ showlines:true, coloring:'heatmap', showlabels:true, labelfont:{ size:9, color:'white' } },
            line:{ width:0.5, color:'rgba(255,255,255,0.25)' },
            hovertemplate:`${xAxis}: %{x}<br>${yAxis}: %{y}<br>${formatPropertyName(property)}: %{z:.4f}<extra></extra>` };
    });

    // Distribute colorbar positions vertically across the plot
    const allY = yCoords;
    const yDataMin = Math.min(...allY), yDataMax = Math.max(...allY);
    const coloraxisLayouts: Record<string, any> = {};
    segments.forEach((seg, si) => {
      const segYCoords = yCoords.slice(seg.startIdx, seg.endIdx + 1);
      let sMin = Infinity, sMax = -Infinity;
      segYCoords.forEach(yc => xCoords.forEach(xc => {
        const v = dataMap.get(ck(xc, yc));
        if (v != null && isFinite(v)) { sMin = Math.min(sMin, v); sMax = Math.max(sMax, v); }
      }));
      if (sMin >= sMax) { sMin -= 1; sMax += 1; }
      const yBandMin = Math.min(...segYCoords), yBandMax = Math.max(...segYCoords);
      const fracT = (yBandMin - yDataMin) / (yDataMax - yDataMin);
      const fracB = (yBandMax - yDataMin) / (yDataMax - yDataMin);
      // Plotly colorbar y/len are in paper coords (0–1 of plot area, approx 0.1–0.9 of figure)
      const plotAreaT = 0.1, plotAreaB = 0.9, plotAreaH = plotAreaB - plotAreaT;
      const cbT = yAxis === 'z'
        ? plotAreaT + fracT * plotAreaH
        : plotAreaB - fracB * plotAreaH;
      const cbB = yAxis === 'z'
        ? plotAreaT + fracB * plotAreaH
        : plotAreaB - fracT * plotAreaH;
      const cbLen = Math.max(0.05, cbB - cbT);
      const cbYAnchor = yAxis === 'z' ? cbT : cbB;
      const key = coloraxisKeys[si];
      coloraxisLayouts[key] = {
        colorscale: 'Viridis',
        cmin: sMin, cmax: sMax,
        showscale: true,
        colorbar: {
          y: cbYAnchor + cbLen / 2,
          len: cbLen,
          yanchor: 'middle',
          thickness: 12,
          tickfont: { size: 8 },
          title: { text: `L${si+1}`, font: { size: 9 }, side: 'right' },
        },
      };
    });

    const layout: any = {
      title: { text:'', pad:{ t:0 } },
      margin: { l:60, r:80, t:20, b:55 },
      xaxis: {
        title:{ text:`${xAxis.toUpperCase()} coordinate${coordSuffix}`, font:{ size:12 }, standoff:8 },
        showgrid:true, gridcolor:'rgba(0,0,0,0.06)', tickfont:{ size:10 }, fixedrange: true,
      },
      yaxis: {
        title:{ text:`${yAxis.toUpperCase()} coordinate${coordSuffix}`, font:{ size:12 }, standoff:8 },
        autorange: yAxis==='z' ? 'reversed' : true,
        showgrid:true, gridcolor:'rgba(0,0,0,0.06)', tickfont:{ size:10 }, fixedrange: true,
      },
      ...coloraxisLayouts,
      shapes, annotations,
    };
    Plotly.newPlot(containerId, traces, layout, cfg);
  }
}

// ── NumInput ───────────────────────────────────────────────────────────────────

const NumInput: React.FC<{
  value: number; onChange: (n: number) => void;
  className?: string; step?: string; min?: string;
}> = ({ value, onChange, className, step, min }) => {
  const [draft, setDraft] = useState<string>(String(value));
  const focused = useRef(false);
  useEffect(() => { if (!focused.current) setDraft(String(value)); }, [value]);
  const commit = () => {
    focused.current = false;
    const n = parseFloat(draft);
    if (!isNaN(n)) { onChange(n); setDraft(String(n)); } else setDraft(String(value));
  };
  return (
    <input type="text" inputMode="decimal" step={step} min={min} value={draft}
      onChange={e => setDraft(e.target.value)}
      onFocus={() => { focused.current = true; }}
      onBlur={commit}
      onKeyDown={e => { if (e.key==='Enter') e.currentTarget.blur(); }}
      className={className}
    />
  );
};

// ── UnitInput ────────────────────────────────────────────────────────────────
// A units-aware numeric field. It ALWAYS reports its value back to the parent
// in stored (US-customary) units, and displays it converted to the current
// system. While focused it holds a free-text draft so the user can type
// intermediate strings ("132.") without the value being mangled by the
// display<->store round-trip. Live updates still fire on each keystroke so
// validation stays responsive. Pass dim = 'none' for dimensionless fields.

const UnitInput: React.FC<{
  usValue: number | null;              // stored value (US customary), or null
  dim: string;                         // dimension key, or 'none' for dimensionless
  units: UseUnits;
  onChange: (us: number | null) => void;
  className?: string;
  placeholder?: string;
}> = ({ usValue, dim, units, onChange, className, placeholder }) => {
  const focused = useRef(false);
  const convert = dim !== 'none' && dim !== '';

  const toDraft = useCallback(
    (us: number | null): string =>
      us == null ? '' : formatNum(convert ? units.toDisplay(dim, us) : us),
    [convert, dim, units],
  );

  const [draft, setDraft] = useState<string>(() => toDraft(usValue));

  // Re-sync the draft from the stored value whenever it changes or the unit
  // system flips — but never while the user is actively typing in this field.
  useEffect(() => {
    if (!focused.current) setDraft(toDraft(usValue));
  }, [usValue, units.system, toDraft]);

  const pushLive = (raw: string) => {
    if (raw.trim() === '') { onChange(null); return; }
    const d = parseFloat(raw);
    if (isNaN(d)) return; // keep the draft, don't push NaN upstream
    onChange(convert ? units.fromDisplay(dim, d) : d);
  };

  return (
    <input
      type="text"
      inputMode="decimal"
      className={className}
      placeholder={placeholder}
      value={draft}
      onChange={e => { setDraft(e.target.value); pushLive(e.target.value); }}
      onFocus={() => { focused.current = true; }}
      onBlur={() => { focused.current = false; setDraft(toDraft(usValue)); }}
      onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
    />
  );
};

// ── ConvertInput ─────────────────────────────────────────────────────────────
// Like UnitInput, but takes explicit display<->stored converters instead of a
// dimension key. Used where the displayed quantity is a function of the stored
// value (e.g. area from radius, or resultant load from pressure and radius).
// resyncKey should change whenever the converters change (unit switches) so the
// draft re-syncs while the field is not being edited.

const ConvertInput: React.FC<{
  usValue: number | null;
  toDisplay: (us: number) => number;
  fromDisplay: (disp: number) => number;
  resyncKey: string;
  onChange: (us: number | null) => void;
  className?: string;
  placeholder?: string;
}> = ({ usValue, toDisplay, fromDisplay, resyncKey, onChange, className, placeholder }) => {
  const focused = useRef(false);
  const toDraft = (us: number | null): string => (us == null || !isFinite(us) ? '' : formatNum(toDisplay(us)));
  const [draft, setDraft] = useState<string>(() => toDraft(usValue));

  useEffect(() => {
    if (!focused.current) setDraft(toDraft(usValue));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usValue, resyncKey]);

  const pushLive = (raw: string) => {
    if (raw.trim() === '') { onChange(null); return; }
    const d = parseFloat(raw);
    if (isNaN(d)) return;
    onChange(fromDisplay(d));
  };

  return (
    <input
      type="text"
      inputMode="decimal"
      className={className}
      placeholder={placeholder}
      value={draft}
      onChange={e => { setDraft(e.target.value); pushLive(e.target.value); }}
      onFocus={() => { focused.current = true; }}
      onBlur={() => { focused.current = false; setDraft(toDraft(usValue)); }}
      onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
    />
  );
};

// ── UnitToggle ───────────────────────────────────────────────────────────────
// Small sliding switch: US Customary (default) <-> Metric.

const UnitToggle: React.FC<{ system: UnitSystem; onToggle: () => void }> = ({ system, onToggle }) => {
  const isMetric = system === 'metric';
  return (
    <button
      type="button"
      role="switch"
      aria-checked={isMetric}
      aria-label="Toggle unit system"
      onClick={onToggle}
      className="relative flex items-center rounded-full bg-white/10 border border-white/20 p-0.5 select-none cursor-pointer shrink-0"
      style={{ width: 196, height: 32 }}
    >
      <span
        className="absolute top-0.5 bottom-0.5 rounded-full bg-[#FFB81C] shadow transition-all duration-300 ease-out"
        style={{ width: '48%', left: isMetric ? '50%' : '2%' }}
      />
      <span className={`relative z-10 flex-1 text-center text-xs font-semibold transition-colors duration-300 ${!isMetric ? 'text-[#001f5b]' : 'text-white/70'}`}>
        US Customary
      </span>
      <span className={`relative z-10 flex-1 text-center text-xs font-semibold transition-colors duration-300 ${isMetric ? 'text-[#001f5b]' : 'text-white/70'}`}>
        Metric
      </span>
    </button>
  );
};

// ── UnitSettings ─────────────────────────────────────────────────────────────
// Gear button next to the toggle. Opens a modal to pick which metric unit to
// use per dimension (e.g. cm/mm/m, kg/cm²/kPa/MPa). Selecting a unit never
// changes the stored value; it only changes how metric values are displayed.

const DIM_LABELS: Record<string, string> = {
  length: 'Distance', pressure: 'Pressure', modulus: 'Modulus', force: 'Force',
};

const UnitSettings: React.FC<{ units: UseUnits }> = ({ units }) => {
  const [open, setOpen] = useState(false);
  // Only dimensions that actually offer a choice in metric are configurable.
  const configurable = Object.keys(units.dimensions)
    .filter(d => units.options(d, 'metric').length > 1);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Unit preferences"
        aria-label="Unit preferences"
        className="flex items-center justify-center text-white/70 hover:text-white transition-colors shrink-0"
        style={{ width: 32, height: 32 }}
      >
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor"
          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="14" cy="6" r="2" />
          <path d="M4 6h8M16 6h4" />
          <circle cx="8" cy="12" r="2" />
          <path d="M4 12h2M10 12h10" />
          <circle cx="17" cy="18" r="2" />
          <path d="M4 18h11M19 18h1" />
        </svg>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setOpen(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6"
            style={{ fontFamily: "'Outfit', sans-serif" }}
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-1">
              <h3 style={serifFont} className="text-xl font-semibold text-[#001f5b]">Unit Preferences</h3>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close"
                className="text-gray-400 hover:text-gray-600 -mt-1 -mr-1 p-1"
              >
                <svg viewBox="0 0 20 20" fill="currentColor" width="18" height="18">
                  <path d="M6 6l8 8M14 6l-8 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                </svg>
              </button>
            </div>
            <p className="text-xs text-gray-500 mb-5 leading-relaxed">
              Choose which metric unit to display for each quantity. Stored values never
              change, only how they are shown, so switching is always exact.
            </p>

            <div className="space-y-5">
              {configurable.map(dim => {
                const opts = units.options(dim, 'metric');
                const sel  = units.selectedSymbol(dim, 'metric');
                return (
                  <div key={dim}>
                    <label style={monoFont} className="block text-[0.7rem] font-semibold text-gray-500 tracking-wide mb-2">
                      {DIM_LABELS[dim] ?? dim}
                    </label>
                    <div className="flex gap-2 flex-wrap">
                      {opts.map(o => {
                        const active = o.symbol === sel;
                        return (
                          <button
                            key={o.symbol}
                            type="button"
                            onClick={() => units.setUnit(dim, o.symbol, 'metric')}
                            title={o.name}
                            className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                              active
                                ? 'bg-[#003594] text-white border-[#003594] shadow-sm'
                                : 'bg-white text-gray-600 border-gray-200 hover:border-[#003594]/40 hover:text-[#003594]'
                            }`}
                          >
                            {o.symbol}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="px-5 py-2 rounded-lg bg-[#003594] text-white font-semibold text-sm hover:bg-[#00276e] transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ── Sub-components ─────────────────────────────────────────────────────────────

const EngineIndicator: React.FC<{ status: ReturnType<typeof useMnLayer>['status'] }> = ({ status }) => {
  const cfg = {
    initializing: { dot:'bg-yellow-400 animate-pulse', text: status.kind==='initializing' ? status.message : 'Initializing…', cls:'text-yellow-600' },
    ready:        { dot:'bg-green-500',                text:'Ready',  cls:'text-green-600' },
    running:      { dot:'bg-blue-400 animate-pulse',   text:'Running…',      cls:'text-blue-500'  },
    done:         { dot:'bg-green-500',                text:'Done',          cls:'text-green-600' },
    error:        { dot:'bg-red-500',                  text: status.kind==='error' ? status.message : 'Error', cls:'text-red-500' },
  };
  const c = cfg[status.kind];
  return (
    <div className="flex items-center gap-2">
      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${c.dot}`} />
      <span style={monoFont} className={`text-xs max-w-xs truncate ${c.cls}`}>{c.text}</span>
    </div>
  );
};

const Instructions: React.FC = () => {
  const [open, setOpen] = useState(false);
  return (
    <section className="bg-white rounded-2xl border border-gray-200 shadow-[0_2px_12px_rgba(0,53,148,0.06)] overflow-hidden">
      <button onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50/60 transition-colors">
        <div className="flex items-center gap-3">
          <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16" className="text-[#003594] flex-shrink-0">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <span style={serifFont} className="font-semibold text-[#001f5b] text-lg">Instructions &amp; Parameter Definitions</span>
        </div>
        <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16"
          className={`text-gray-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}>
          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </button>
      {open && (
        <div className="px-6 pb-6 border-t border-gray-100 space-y-5 pt-5">
          <p className="text-sm text-gray-600 leading-relaxed">
            MnLayer is a high-performance layered elastic analysis program for flexible (asphalt) pavement systems.
            Define the pavement structure, apply circular loads, and choose between a field visualization or individual
            analysis points to obtain stresses, strains, and displacements throughout the system.

          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { title: 'Layers', items: [
                { term: 'Elastic Modulus', def: 'Stiffness of the layer material, in psi.' },
                { term: 'Poisson Ratio',   def: 'Ratio of lateral to axial strain (dimensionless). Typical range: 0.10–0.499.' },
                { term: 'Thickness',       def: 'Layer depth in inches. Not required for the last (subgrade) layer.' },
                { term: 'Bond Coefficient',def: 'Interface bonding. Use 0 for fully bonded, 1000 for fully unbonded.' },
              ]},
              { title: 'Loads', items: [
                { term: 'Pressure',         def: 'Uniform contact pressure in psi. Typical tire pressure: 80–120 psi.' },
                { term: 'Radius',           def: 'Radius of the circular loaded area in inches.' },
                { term: 'X, Y Coordinates', def: 'Horizontal position of the load center in the XY plane.' },
              ]},
              { title: 'Analysis Modes', items: [
                { term: 'Field Plot',       def: 'Generates an automatic grid with interface pairs. Choose XZ, YZ, or XY plane and a property to visualize as a heatmap or contour plot.' },
                { term: 'Individual Points',def: 'Specify exact XYZ coordinates to get full stress, strain, and displacement tables at each location.' },
                { term: 'Principal Stresses',def: 'σ₁, σ₂, σ₃ are computed automatically in the XZ plane (y=0) from the stress tensor at each point.' },
              ]},
            ].map(({ title, items }) => (
              <div key={title} className="bg-gray-50 rounded-xl p-4 space-y-2">
                <h3 style={serifFont} className="font-semibold text-[#001f5b] text-base mb-3">{title}</h3>
                {items.map(({ term, def }) => (
                  <div key={term}>
                    <span style={monoFont} className="text-xs font-medium text-[#003594]">{term}</span>
                    <p className="text-xs text-gray-500 leading-relaxed mt-0.5">{def}</p>
                  </div>
                ))}
              </div>
            ))}
          </div>
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <p style={monoFont} className="text-[0.65rem] text-[#003594] uppercase tracking-widest mb-1">Cite as</p>
              <p className="text-xs text-gray-600 leading-relaxed">
                Khazanovich, L., &amp; Wang, Q. (2007). MnLayer: high-performance layered elastic analysis program.{' '}
                <em>Transportation Research Record</em>, Vol. 2037 No. 1, pp. 63–75, doi: 10.3141/2037-06
              </p>
            </div>
            <a href="https://journals.sagepub.com/doi/10.3141/2037-06" target="_blank" rel="noopener noreferrer"
              className="flex-shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#003594] text-white text-xs font-semibold hover:bg-[#00276e] transition-colors whitespace-nowrap">
              Read Paper
              <svg viewBox="0 0 20 20" fill="currentColor" width="12" height="12">
                <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
                <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
              </svg>
            </a>
          </div>
        </div>
      )}
    </section>
  );
};

const SectionHeader: React.FC<{
  title: string; count: number; min: number; max: number; onChange: (n: number) => void;
  rightSlot?: React.ReactNode;
}> = ({ title, count, min, max, onChange, rightSlot }) => (
  <div className="flex items-center gap-3 mb-4">
    <select value={count} onChange={e => onChange(Number(e.target.value))} className={selectCls}>
      {Array.from({ length: max - min + 1 }, (_, i) => i + min).map(n => (
        <option key={n} value={n}>{n}</option>
      ))}
    </select>
    <h2 style={serifFont} className="text-xl font-semibold text-[#001f5b]">{title}</h2>
    {rightSlot}
  </div>
);

// ── BondInput ──────────────────────────────────────────────────────────────────
// Shows "(bonded)" or "(unbonded)" hint beside the field when blurred and the
// value is exactly 0 or 1000. While focused it's a plain number field.

const BondInput: React.FC<{
  value: number | null;
  onChange: (v: number | null) => void;
  className?: string;
}> = ({ value, onChange, className }) => {
  const [focused, setFocused] = useState(false);

  const hint = !focused && value === 0    ? 'bonded'
             : !focused && value === 1000 ? 'unbonded'
             : null;

  return (
    <div className="flex items-center gap-1.5">
      <input
        type="number"
        min="0"
        step="1"
        className={className}
        value={value ?? ''}
        onChange={e => onChange(e.target.value === '' ? null : Number(e.target.value))}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
      />
      {hint && (
        <span style={{ fontFamily: "'DM Mono', monospace" }}
          className="text-xs text-[#003594]/60 whitespace-nowrap select-none">
          ({hint})
        </span>
      )}
    </div>
  );
};

const LayersTable: React.FC<{ layers: Layer[]; errors: LayerError[]; units: UseUnits; onChange: (l: Layer[]) => void }> = ({ layers, errors, units, onChange }) => {
  // Poisson ratio is dimensionless; edit it as a plain string.
  const update = (idx: number, field: keyof Layer, val: string) =>
    onChange(layers.map((l, i) => i === idx ? { ...l, [field]: val==='' ? null : Number(val) } : l));
  // Convertible fields report back in stored (US) units already.
  const setNum = (idx: number, field: keyof Layer, us: number | null) =>
    onChange(layers.map((l, i) => i === idx ? { ...l, [field]: us } : l));
  const headers = ['#', `Elastic Modulus, ${units.symbol('pressure')}`, 'Poisson Ratio', `Thickness, ${units.symbol('length')}`, 'Bond Coefficient (0-1000)'];
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead><tr>{headers.map(h => <th key={h} className={thCls}>{h}</th>)}</tr></thead>
        <tbody className="divide-y divide-gray-100 bg-white">
          {layers.map((layer, idx) => {
            const isLast = idx === layers.length - 1;
            const err = errors[idx] ?? {};
            return (
              <tr key={idx} className="hover:bg-gray-50/50 transition-colors">
                <td className={numTd}>{idx+1}</td>
                <td className={tdCls}><UnitInput usValue={layer.elastic_modulus} dim="pressure" units={units} className={err.elastic_modulus ? inputErrCls : inputCls} onChange={us=>setNum(idx,'elastic_modulus',us)} /></td>
                <td className={tdCls}><input type="number" min="0" step="0.01" className={err.poisson_ratio ? inputErrCls : inputCls} value={layer.poisson_ratio??''} onChange={e=>update(idx,'poisson_ratio',e.target.value)} /></td>
                <td className={tdCls}>{isLast ? <span className="text-sm text-gray-300 italic pl-1">—</span> : <UnitInput usValue={layer.thickness} dim="length" units={units} className={err.thickness ? inputErrCls : inputCls} onChange={us=>setNum(idx,'thickness',us)} />}</td>
                <td className={tdCls}>{isLast ? <span className="text-sm text-gray-300 italic pl-1">—</span> : <BondInput value={layer.bond_coefficient} onChange={v => setNum(idx, 'bond_coefficient', v)} className={err.bond_coefficient ? inputErrCls : inputCls} />}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export type LoadInputMode = 'load_radius' | 'pressure_radius' | 'pressure_load' | 'load_area' | 'pressure_area';

interface LoadModeSpec { compute: 'pressure' | 'load' | 'geometry'; geom: 'radius' | 'area'; label: string; }

// Each mode says which two quantities the user enters (the third is computed)
// and whether geometry is expressed as radius or area.
const LOAD_MODE_SPEC: Record<LoadInputMode, LoadModeSpec> = {
  load_radius:     { compute: 'pressure', geom: 'radius', label: 'Load + radius' },
  pressure_radius: { compute: 'load',     geom: 'radius', label: 'Pressure + radius' },
  pressure_load:   { compute: 'geometry', geom: 'radius', label: 'Pressure + load' },
  load_area:       { compute: 'pressure', geom: 'area',   label: 'Load + area' },
  pressure_area:   { compute: 'load',     geom: 'area',   label: 'Pressure + area' },
};
const LOAD_MODE_ORDER: LoadInputMode[] = ['load_radius', 'pressure_radius', 'pressure_load', 'load_area', 'pressure_area'];

// Positive-and-finite guard. Used so a zero (or blank) in any field can never
// produce a divide-by-zero: the dependent quantity becomes null (blank + a
// validation error) instead of Infinity/NaN.
const okPos = (n: number | null | undefined): n is number => n != null && isFinite(n) && n > 0;
const forceFromPr = (P: number | null, r: number | null): number | null =>
  okPos(P) && okPos(r) ? P * Math.PI * r * r : null;
const pressureFromFr = (F: number | null, r: number | null): number | null =>
  okPos(F) && okPos(r) ? F / (Math.PI * r * r) : null;
const radiusFromFP = (F: number | null, P: number | null): number | null =>
  okPos(F) && okPos(P) ? Math.sqrt((F / P) / Math.PI) : null;

// One row's load definition, in US base units. All three are kept, with the
// two the user enters preserved verbatim and the third derived, so clearing an
// input never wipes another. pressure + radius (the engine's inputs) are pushed
// to the parent on every edit.
type LoadRowDef = { pressure: number | null; radius: number | null; load: number | null };
const makeDef = (l: Load): LoadRowDef => ({ pressure: l.pressure, radius: l.radius, load: forceFromPr(l.pressure, l.radius) });

const LoadsTable: React.FC<{ loads: Load[]; units: UseUnits; mode: LoadInputMode; onChange: (l: Load[]) => void; onValidityChange?: (invalid: boolean) => void }> = ({ loads, units, mode, onChange, onValidityChange }) => {
  const { compute, geom } = LOAD_MODE_SPEC[mode];

  const [defs, setDefs] = useState<LoadRowDef[]>(() => loads.map(makeDef));
  // Keep one def per load as the count changes; new rows seed from the load.
  // Runs on any loads change but is a no-op (returns the same defs) unless the
  // row count actually changed, so it never clobbers in-progress entries.
  useEffect(() => {
    setDefs(prev => (prev.length === loads.length ? prev : loads.map((l, i) => prev[i] ?? makeDef(l))));
  }, [loads]);

  const patchLoad = (i: number, patch: Partial<Load>) =>
    onChange(loads.map((l, k) => (k === i ? { ...l, ...patch } : l)));

  // Set the edited quantity, recompute the one this mode derives, then push the
  // engine values (pressure + radius) up. The two entered quantities are never
  // overwritten by each other.
  const updateDef = (idx: number, patch: Partial<LoadRowDef>) => {
    const d: LoadRowDef = { ...(defs[idx] ?? { pressure: null, radius: null, load: null }), ...patch };
    if (compute === 'pressure') d.pressure = pressureFromFr(d.load, d.radius);
    else if (compute === 'load') d.load = forceFromPr(d.pressure, d.radius);
    else d.radius = radiusFromFP(d.load, d.pressure);
    setDefs(prev => prev.map((x, i) => (i === idx ? d : x)));
    patchLoad(idx, { pressure: d.pressure, radius: d.radius });
  };

  const lenSym = units.symbol('length');
  const preSym = units.symbol('pressure');
  const forceSym = units.symbol('force');
  const geomSym = geom === 'radius' ? lenSym : `${lenSym}²`;
  const geomName = geom === 'radius' ? 'Radius' : 'Area';

  // Display <-> stored (US) converters.
  const toP = (us: number) => units.toDisplay('pressure', us);
  const frP = (d: number) => units.fromDisplay('pressure', d);
  const toL = (us: number) => units.toDisplay('length', us);
  const frL = (d: number) => units.fromDisplay('length', d);
  const toForce = (us: number) => units.toDisplay('force', us);
  const frForce = (d: number) => units.fromDisplay('force', d);
  const toArea = (rus: number) => Math.PI * toL(rus) ** 2;
  const frArea = (A: number) => frL(Math.sqrt(Math.max(A, 0) / Math.PI));
  const geomTo = geom === 'area' ? toArea : toL;
  const geomFr = geom === 'area' ? frArea : frL;

  const pKey = `p:${units.system}:${units.selectedSymbol('pressure')}`;
  const fKey = `f:${units.system}:${units.selectedSymbol('force')}`;
  const gKey = `g:${units.system}:${units.selectedSymbol('length')}:${geom}`;

  // Only the two entered quantities are validated; the derived one follows from
  // them, so it is never flagged on its own.
  const needP = compute !== 'pressure';
  const needG = compute !== 'geometry';
  const needL = compute !== 'load';
  const rowErrors = defs.map(d => ({
    pressure: needP && !okPos(d.pressure),
    radius: needG && !okPos(d.radius),
    load: needL && !okPos(d.load),
  }));
  const invalid = rowErrors.some(e => e.pressure || e.radius || e.load);
  useEffect(() => { onValidityChange?.(invalid); }, [invalid, onValidityChange]);

  const messages: string[] = [];
  rowErrors.forEach((e, i) => {
    const prefix = loads.length > 1 ? `Load ${i + 1}: ` : '';
    if (e.pressure) messages.push(`${prefix}Pressure must be > 0`);
    if (e.load) messages.push(`${prefix}Load must be > 0`);
    if (e.radius) messages.push(`${prefix}${geomName} must be > 0`);
  });

  const computedCell = (value: string) => <input readOnly className={inputComputedCls} value={value} />;

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead>
          <tr>
            <th className={thCls}>#</th>
            <th className={thCls}>Pressure, {preSym}</th>
            <th className={thCls}>{geomName}, {geomSym}</th>
            <th className={thCls}>Load, {forceSym}</th>
            <th className={thCls}>X, {lenSym}</th>
            <th className={thCls}>Y, {lenSym}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 bg-white">
          {loads.map((load, idx) => {
            const d = defs[idx] ?? makeDef(load);
            const e = rowErrors[idx] ?? { pressure: false, radius: false, load: false };
            return (
              <tr key={idx} className="hover:bg-gray-50/50 transition-colors">
                <td className={numTd}>{idx + 1}</td>

                <td className={tdCls}>
                  {compute === 'pressure'
                    ? computedCell(okPos(d.pressure) ? formatNum(toP(d.pressure)) : '—')
                    : <ConvertInput usValue={d.pressure} toDisplay={toP} fromDisplay={frP} resyncKey={pKey}
                        className={e.pressure ? inputErrCls : inputCls} onChange={v => updateDef(idx, { pressure: v })} />}
                </td>

                <td className={tdCls}>
                  {compute === 'geometry'
                    ? computedCell(okPos(d.radius) ? formatNum(geomTo(d.radius)) : '—')
                    : <ConvertInput usValue={d.radius} toDisplay={geomTo} fromDisplay={geomFr} resyncKey={gKey}
                        className={e.radius ? inputErrCls : inputCls} onChange={v => updateDef(idx, { radius: v })} />}
                </td>

                <td className={tdCls}>
                  {compute === 'load'
                    ? computedCell(okPos(d.load) ? formatNum(toForce(d.load)) : '—')
                    : <ConvertInput usValue={d.load} toDisplay={toForce} fromDisplay={frForce} resyncKey={fKey}
                        className={e.load ? inputErrCls : inputCls} onChange={v => updateDef(idx, { load: v })} />}
                </td>

                <td className={tdCls}><UnitInput usValue={load.x_coordinate} dim="length" units={units} className={inputCls} onChange={us => patchLoad(idx, { x_coordinate: us })} /></td>
                <td className={tdCls}><UnitInput usValue={load.y_coordinate} dim="length" units={units} className={inputCls} onChange={us => patchLoad(idx, { y_coordinate: us })} /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {messages.length > 0 && (
        <div className="mt-3 flex flex-col gap-0.5">
          {messages.map(m => <p key={m} style={monoFont} className="text-xs text-red-500">⚠ {m}</p>)}
        </div>
      )}
    </div>
  );
};

const PointsTable: React.FC<{ points: AnalysisPoint[]; errors: PointError[]; layers: Layer[]; units: UseUnits; onChange: (p: AnalysisPoint[]) => void }> = ({ points, errors, layers, units, onChange }) => {
  // Layer number is a dimensionless integer.
  const update = (idx: number, field: keyof AnalysisPoint, val: string) =>
    onChange(points.map((p, i) => i === idx ? { ...p, [field]: val==='' ? null : Number(val) } : p));
  const setNum = (idx: number, field: keyof AnalysisPoint, us: number | null) =>
    onChange(points.map((p, i) => i === idx ? { ...p, [field]: us } : p));
  const lenSym = units.symbol('length');
  const headers = ['#', 'Layer', `X, ${lenSym}`, `Y, ${lenSym}`, `Z (from layer top), ${lenSym}`];
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead><tr>{headers.map(h => <th key={h} className={thCls}>{h}</th>)}</tr></thead>
        <tbody className="divide-y divide-gray-100 bg-white">
          {points.map((pt, idx) => {
            const err = errors[idx] ?? {};
            // Show the layer thickness limit (in display units) as a placeholder hint
            const ln = Number(pt.layer_number);
            const isValidLayer = !isNaN(ln) && ln >= 1 && ln <= layers.length;
            const isLastLayer  = isValidLayer && ln === layers.length;
            const layerTh = isValidLayer && !isLastLayer ? Number(layers[ln-1].thickness) : null;
            const zPlaceholder = layerTh && layerTh > 0 ? `0 – ${formatNum(units.toDisplay('length', layerTh))}` : '0';
            return (
              <tr key={idx} className="hover:bg-gray-50/50 transition-colors">
                <td className={numTd}>{idx+1}</td>
                <td className={tdCls}><input type="number" min="1" className={err.layer_number ? inputErrCls : inputCls} value={pt.layer_number??''} onChange={e=>update(idx,'layer_number',e.target.value)} /></td>
                <td className={tdCls}><UnitInput usValue={pt.x_coordinate} dim="length" units={units} className={inputCls} onChange={us=>setNum(idx,'x_coordinate',us)} /></td>
                <td className={tdCls}><UnitInput usValue={pt.y_coordinate} dim="length" units={units} className={inputCls} onChange={us=>setNum(idx,'y_coordinate',us)} /></td>
                <td className={tdCls}><UnitInput usValue={pt.z_coordinate} dim="length" units={units} placeholder={zPlaceholder} className={err.z_coordinate ? inputErrCls : inputCls} onChange={us=>setNum(idx,'z_coordinate',us)} /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const ResultsTable: React.FC<{ results: ResultPoint[]; units: UseUnits; notation: ResultNotation }> = ({ results, units, notation }) => {
  // Results are stored in US units; convert only for display. Strains are
  // dimensionless and shown unchanged in either system.
  const fmt = notation === 'scientific' ? fmtSci : (v: number) => fmtDecimal(v);
  const P = (v: number) => fmt(units.toDisplay('pressure', v)); // stresses
  const L = (v: number) => fmt(units.toDisplay('length', v));   // displacements
  const C = (v: number) => fmtCoord(units.toDisplay('length', v)); // coordinates
  const lenSym = units.symbol('length');
  const preSym = units.symbol('pressure');
  // Right-aligned so the minus sign hangs left and digits line up column-wise.
  const thNum = thCls.replace('text-left', 'text-right');
  const numCell = 'px-3 py-2 font-mono text-xs text-gray-700 text-right tabular-nums';
  const labelCell = 'px-3 py-2 text-sm font-semibold text-gray-500 tracking-wide whitespace-nowrap';
  return (
    <div className="space-y-6">
      {results.map((pt, idx) => (
        <div key={idx} className="space-y-2">
          <p style={monoFont} className="text-xs font-medium text-[#001f5b] bg-blue-50 px-3 py-2 rounded-lg">
            Point {idx+1} — Layer {pt.layer} — XYZ ({C(pt.x)}, {C(pt.y)}, {C(pt.z)}) {lenSym}
          </p>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead><tr>{['','XX','YY','ZZ','XY','XZ','YZ'].map((h, i) => <th key={h} className={i === 0 ? thCls : thNum}>{h}</th>)}</tr></thead>
              <tbody className="divide-y divide-gray-100">
                <tr className="hover:bg-gray-50/50">
                  <td className={labelCell}>Stress, {preSym}</td>
                  {(['stress_xx','stress_yy','stress_zz','stress_xy','stress_zx','stress_yz'] as const).map(k => (
                    <td key={k} className={numCell}>{P(pt[k])}</td>
                  ))}
                </tr>
                <tr className="hover:bg-gray-50/50">
                  <td className={labelCell}>Strain</td>
                  {(['strain_xx','strain_yy','strain_zz','strain_xy','strain_zx','strain_yz'] as const).map(k => (
                    <td key={k} className={numCell}>{fmt(pt[k])}</td>
                  ))}
                </tr>
                <tr className="hover:bg-gray-50/50">
                  <td className={labelCell}>Displacement, {lenSym}</td>
                  <td className={numCell}>{L(pt.horizontal_displacement_x)}</td>
                  <td className={numCell}>{L(pt.horizontal_displacement_y)}</td>
                  <td className={numCell}>{L(pt.vertical_displacement)}</td>
                  <td colSpan={3} />
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
};

// Sliding switch between scientific and plain-decimal number formatting.
const NotationToggle: React.FC<{ value: ResultNotation; onChange: (v: ResultNotation) => void }> = ({ value, onChange }) => {
  const isDec = value === 'decimal';
  return (
    <button
      type="button"
      role="switch"
      aria-checked={isDec}
      aria-label="Number format"
      onClick={() => onChange(isDec ? 'scientific' : 'decimal')}
      className="relative flex items-center rounded-full bg-gray-100 border border-gray-200 p-0.5 select-none cursor-pointer shrink-0"
      style={{ width: 184, height: 30 }}
    >
      <span
        className="absolute top-0.5 bottom-0.5 rounded-full bg-[#003594] shadow transition-all duration-300 ease-out"
        style={{ width: '48%', left: isDec ? '50%' : '2%' }}
      />
      <span className={`relative z-10 flex-1 text-center text-xs font-semibold transition-colors duration-300 ${!isDec ? 'text-white' : 'text-gray-500'}`}>Scientific</span>
      <span className={`relative z-10 flex-1 text-center text-xs font-semibold transition-colors duration-300 ${isDec ? 'text-white' : 'text-gray-500'}`}>Decimal</span>
    </button>
  );
};

const PLANE_IMAGES: Record<PlotType, string> = {
  XZ: '/planes-xz.png',
  YZ: '/planes-yz.png',
  XY: '/planes-xy.png',
};

const PLOT_TYPES: PlotType[] = ['XZ', 'YZ', 'XY'];

const GridConfigPanel: React.FC<{ config: GridConfig; units: UseUnits; onChange: (c: GridConfig) => void }> = ({ config, units, onChange }) => {
  const set = (patch: Partial<GridConfig>) => onChange({ ...config, ...patch });
  const { plotType } = config;
  const lenSym = units.symbol('length');

  return (
    <div className="flex items-start gap-6">

      {/* ── Left: plane diagram (crossfade between all three) ── */}
      <div className="relative flex-shrink-0" style={{ width: 230, height: 300 }}>
        {PLOT_TYPES.map(pt => (
          <img
            key={pt}
            src={PLANE_IMAGES[pt]}
            alt={`${pt} plane diagram`}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              opacity: plotType === pt ? 1 : 0,
              transition: 'opacity 0.35s ease',
              pointerEvents: 'none',
            }}
          />
        ))}
      </div>

      {/* ── Right: controls ── */}
      <div className="space-y-4 pt-6">
        <div className="flex items-center gap-4">
          <label style={monoFont} className="text-xs font-semibold text-gray-500 uppercase tracking-wider w-24">Plot Type</label>
          <select value={plotType} onChange={e => set({ plotType: e.target.value as PlotType })} className={selectCls}>
            <option value="XZ">XZ Plane</option>
            <option value="YZ">YZ Plane</option>
            <option value="XY">XY Plane</option>
          </select>
        </div>

        {(plotType === 'XZ' || plotType === 'YZ') && (
          <div className="space-y-3 ml-4 border-l-2 border-[#003594]/10 pl-4">
            <div className="flex items-center gap-3">
              <label className="text-sm text-gray-600 w-24">{plotType === 'XZ' ? 'X' : 'Y'} Range, {lenSym}</label>
              <UnitInput usValue={config.axis1Min} dim="length" units={units} onChange={v => set({ axis1Min: v ?? 0 })} className={`${inputSmCls} w-24`} />
              <span className="text-sm text-gray-400">to</span>
              <UnitInput usValue={config.axis1Max} dim="length" units={units} onChange={v => set({ axis1Max: v ?? 0 })} className={`${inputSmCls} w-24`} />
            </div>
            <div className="flex items-center gap-3">
              <label className="text-sm text-gray-600 w-24">Z Range, {lenSym}</label>
              <UnitInput usValue={config.axis2Min} dim="length" units={units} onChange={v => set({ axis2Min: v ?? 0 })} className={`${inputSmCls} w-24`} />
              <span className="text-sm text-gray-400">to</span>
              <UnitInput usValue={config.axis2Max} dim="length" units={units} onChange={v => set({ axis2Max: v ?? 0 })} className={`${inputSmCls} w-24`} />
            </div>
            <div className="flex items-center gap-3">
              <label className="text-sm text-gray-600 w-24">{plotType === 'XZ' ? 'Fixed Y' : 'Fixed X'}, {lenSym}</label>
              <UnitInput
                usValue={plotType === 'XZ' ? config.xzFixedY : config.yzFixedX}
                dim="length" units={units}
                onChange={v => set(plotType === 'XZ' ? { xzFixedY: v ?? 0 } : { yzFixedX: v ?? 0 })}
                className={`${inputSmCls} w-24`}
              />
            </div>
          </div>
        )}

        {plotType === 'XY' && (
          <div className="space-y-3 ml-4 border-l-2 border-[#003594]/10 pl-4">
            <div className="flex items-center gap-3">
              <label className="text-sm text-gray-600 w-24">X Range, {lenSym}</label>
              <UnitInput usValue={config.xyXMin} dim="length" units={units} onChange={v => set({ xyXMin: v ?? 0 })} className={`${inputSmCls} w-20`} />
              <span className="text-sm text-gray-400">to</span>
              <UnitInput usValue={config.xyXMax} dim="length" units={units} onChange={v => set({ xyXMax: v ?? 0 })} className={`${inputSmCls} w-20`} />
            </div>
            <div className="flex items-center gap-3">
              <label className="text-sm text-gray-600 w-24">Y Range, {lenSym}</label>
              <UnitInput usValue={config.xyYMin} dim="length" units={units} onChange={v => set({ xyYMin: v ?? 0 })} className={`${inputSmCls} w-20`} />
              <span className="text-sm text-gray-400">to</span>
              <UnitInput usValue={config.xyYMax} dim="length" units={units} onChange={v => set({ xyYMax: v ?? 0 })} className={`${inputSmCls} w-20`} />
            </div>
            <div className="flex items-center gap-3">
              <label className="text-sm text-gray-600 w-24">Layer</label>
              <NumInput value={config.xyLayer} onChange={v => set({ xyLayer: Math.max(1, Math.round(v)) })} className={`${inputSmCls} w-20`} min="1" />
            </div>
            <div className="flex items-center gap-3">
              <label className="text-sm text-gray-600 w-24">Z Coordinate, {lenSym}</label>
              <UnitInput usValue={config.xyZ} dim="length" units={units} onChange={v => set({ xyZ: v ?? 0 })} className={`${inputSmCls} w-20`} />
            </div>

          </div>
        )}
      </div>

    </div>
  );
};

// ── Main page ──────────────────────────────────────────────────────────────────

const MnLayerPlotPage: React.FC = () => {
  const { status, run } = useMnLayer();

  // Unit system (display only). All state below is stored in US customary.
  const units = useUnits();

  // Shared inputs
  const [layers, setLayers] = useState<Layer[]>(DEFAULT_LAYERS);
  const [loads,  setLoads]  = useState<Load[]>(DEFAULT_LOADS);
  const [loadMode, setLoadMode] = useState<LoadInputMode>('load_radius');
  const [loadsInvalid, setLoadsInvalid] = useState(false); // reported by LoadsTable
  const [resultNotation, setResultNotation] = useState<ResultNotation>('scientific');

  // Live validation — recomputed on every render from current state
  const layerErrors = validateLayers(layers);
  // load validity is owned by LoadsTable (it knows the input mode) and reported
  // via setLoadsInvalid; pointErrors computed further down where `points` is in scope

  // Top-level mode toggle
  const [analysisMode, setAnalysisMode] = useState<AnalysisMode>('plot');

  // Plot mode
  const [grid,         setGrid]         = useState<GridConfig>(DEFAULT_GRID);
  const [plotResults,  setPlotResults]  = useState<EnrichedPoint[] | null>(null);
  const [property,     setProperty]     = useState<ResultKey>('strain_zz');
  const [plotMode,     setPlotModeState]= useState<PlotMode>(() => {
    try { return (localStorage.getItem('fem_plot_mode') as PlotMode) || 'curves'; } catch { return 'curves'; }
  });
  const [colorScale, setColorScale] = useState<'global' | 'per-layer'>('global');

  // Points mode
  const [points,       setPoints]       = useState<AnalysisPoint[]>(DEFAULT_POINTS);
  const [pointResults, setPointResults] = useState<ResultPoint[] | null>(null);
  const pointErrors = validatePoints(points, layers, units);

  // Shared
  const [runError, setRunError] = useState<string | null>(null);

  // Refs for Plotly / canvas
  const plotlyRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const plotId    = 'mnlayer-heatmap-plot';

  useEffect(() => {
    if ((window as any).Plotly) return;
    const s = document.createElement('script');
    s.src = 'https://cdn.plot.ly/plotly-latest.min.js';
    document.head.appendChild(s);
  }, []);

  // ── Count adjusters ────────────────────────────────────────────────────────

  const clearResults = () => { setPlotResults(null); setPointResults(null); setRunError(null); };

  const setLayerCount = useCallback((n: number) => {
    clearResults();
    setLayers(prev => {
      if (n === prev.length) return prev;
      if (n > prev.length) {
        const adds = Array.from({ length: n-prev.length }, () => ({ ...DEFAULT_LAYERS[0] }));
        const next = [...prev]; next.splice(prev.length-1, 0, ...adds); return next;
      }
      const next = [...prev]; next.splice(Math.max(1,n-1), prev.length-n); return next.slice(0,n);
    });
  }, []);

  const setLoadCount = useCallback((n: number) => {
    clearResults();
    setLoads(prev => n > prev.length
      ? [...prev, ...Array.from({ length: n-prev.length }, () => ({ ...DEFAULT_LOADS[0] }))]
      : prev.slice(0,n));
  }, []);

  const setPointCount = useCallback((n: number) => {
    setPointResults(null); setRunError(null);
    setPoints(prev => n > prev.length
      ? [...prev, ...Array.from({ length: n-prev.length }, () => ({ ...DEFAULT_POINTS[0] }))]
      : prev.slice(0,n));
  }, []);

  // ── Run — Plot mode ────────────────────────────────────────────────────────

  const handleRunPlot = async () => {
    setRunError(null); setPlotResults(null);
    const pts = generateGridPoints(layers, grid);
    try {
      const raw = await run(layers, loads, pts);
      const enriched: EnrichedPoint[] = raw.map((r, idx) => {
        const z = pts[idx]?.z_coordinate ?? r.z;
        const base = { ...r, _reqZ: z, z };
        return { ...base, ...computePrincipals(base) };
      });
      setPlotResults(enriched);
    } catch (e: any) { setRunError(String(e)); }
  };

  // ── Run — Points mode ──────────────────────────────────────────────────────

  const handleRunPoints = async () => {
    setRunError(null); setPointResults(null);
    // Convert z (from layer top) → z (from surface) before passing to engine
    const layerTops = getLayerTops(layers);
    const globalPoints: AnalysisPoint[] = points.map(pt => ({
      ...pt,
      z_coordinate: (pt.z_coordinate ?? 0) + (layerTops[Number(pt.layer_number ?? 1) - 1] ?? 0),
    }));
    try {
      setPointResults(await run(layers, loads, globalPoints));
    } catch (e: any) { setRunError(String(e)); }
  };

  // ── Re-render plot ─────────────────────────────────────────────────────────

  const axesForPlotType = useMemo((): ['x'|'y'|'z','x'|'y'|'z'] => {
    if (grid.plotType === 'XZ') return ['x','z'];
    if (grid.plotType === 'YZ') return ['y','z'];
    return ['x','y'];
  }, [grid.plotType]);

  useEffect(() => {
    if (!plotResults) return;
    const [xAxis, yAxis] = axesForPlotType;

    // The plot is an output, so it reflects the current display system. We
    // convert coordinates (length) and the selected property (by its own
    // dimension), plus layer thicknesses for the interface lines. Stored state
    // is never mutated here.
    const L = (v: number) => units.toDisplay('length', v);
    const vdim = dimForResultKey(property);
    const V = vdim ? (v: number) => units.toDisplay(vdim, v) : (v: number) => v;
    const dispResults = plotResults.map(r => ({
      ...r,
      x: L(r.x), y: L(r.y), z: L(r.z), _reqZ: L(r._reqZ),
      [property]: V(r[property] as number),
    })) as EnrichedPoint[];
    const dispLayers: Layer[] = layers.map(l => ({
      ...l,
      thickness: l.thickness == null ? null : L(l.thickness),
    }));
    const coordUnit = units.symbol('length');
    const valueUnit = vdim ? units.symbol(vdim) : '';

    if (plotMode === 'canvas') {
      if (plotlyRef.current) plotlyRef.current.style.display = 'none';
      if (canvasRef.current) {
        canvasRef.current.style.display = '';
        canvasRef.current.innerHTML = '';
        renderCanvas(canvasRef.current, dispResults, xAxis, yAxis, property, dispLayers, colorScale, coordUnit, valueUnit);
      }
    } else {
      if (canvasRef.current) canvasRef.current.style.display = 'none';
      if (plotlyRef.current) plotlyRef.current.style.display = '';
      renderPlotly(plotId, dispResults, xAxis, yAxis, property, plotMode, dispLayers, colorScale, coordUnit, valueUnit);
    }
  }, [plotResults, property, plotMode, axesForPlotType, layers, colorScale, units]);

  const cycleMode = useCallback(() => {
    setPlotModeState(prev => {
      const next = MODE_CYCLE[(MODE_CYCLE.indexOf(prev)+1) % MODE_CYCLE.length];
      try { localStorage.setItem('fem_plot_mode', next); } catch {}
      return next;
    });
  }, []);

  const downloadPlot = useCallback(() => {
    const filename = `mnlayer_${property}_${grid.plotType.toLowerCase()}_${units.system}`;
    if (plotMode === 'canvas') {
      const canvas = canvasRef.current?.querySelector('canvas');
      if (!canvas) return;
      const a = document.createElement('a');
      a.href = canvas.toDataURL('image/png');
      a.download = `${filename}.png`;
      a.click();
    } else {
      const Plotly = (window as any).Plotly;
      if (!Plotly) return;
      Plotly.downloadImage(plotId, { format: 'png', width: 1200, height: 700, filename });
    }
  }, [plotMode, property, grid.plotType, units.system]);

  // ── Derived status ─────────────────────────────────────────────────────────

  const isReady   = status.kind === 'ready' || status.kind === 'done';
  const isRunning = status.kind === 'running';
  const inputsValid = !hasErrors(layerErrors) && !loadsInvalid &&
    (analysisMode !== 'points' || !hasErrors(pointErrors));

  const statusText = runError ? runError
    : isRunning ? (analysisMode === 'plot' ? `Running…` : 'Running analysis…')
    : analysisMode === 'plot' && plotResults   ? `Results ready`
    : analysisMode === 'points' && pointResults ? `Results ready`
    : status.kind === 'initializing' ? (status as any).message
    : 'Ready';

  const statusCls = runError ? 'text-red-500'
    : isRunning ? 'text-[#003594] animate-pulse'
    : (plotResults||pointResults) ? 'text-green-600'
    : status.kind==='initializing' ? 'text-yellow-600'
    : 'text-gray-400';

  // Units of what the Field Plot is currently showing (for the caption).
  const plotDim       = dimForResultKey(property);
  const plotValueUnit = plotDim ? units.symbol(plotDim) : 'dimensionless';
  const plotCoordUnit = units.symbol('length');

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-[#f7f6f2]" style={{ fontFamily: "'Outfit', sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Crimson+Pro:ital,wght@0,300;0,400;0,600;1,300;1,400&family=DM+Mono:wght@400;500&family=Outfit:wght@400;500;600&display=swap');`}</style>

      <Navbar />

      <div className="bg-[#001f5b] border-b-2 border-[#FFB81C]">
        <div className="max-w-7xl mx-auto px-6 py-8 flex items-end justify-between">
          <div>
            <p style={monoFont} className="text-[#FFB81C] text-[0.65rem] tracking-[0.16em] uppercase mb-2 opacity-80">
              Flexible Pavements · Analysis
            </p>
            <h1 style={serifFont} className="text-4xl font-light text-white">MnLayer</h1>
            <p className="text-white/50 text-sm mt-1">Multi-layer elastic analysis</p>
          </div>
          <div className="flex flex-col items-end gap-3 pb-1">
            <div className="flex flex-col items-end gap-1.5">
              <span style={monoFont} className="text-[0.6rem] uppercase tracking-[0.14em] text-white/50">Units</span>
              <div className="flex items-center gap-2">
                {units.system === 'metric' && <UnitSettings units={units} />}
                <UnitToggle system={units.system} onToggle={units.toggle} />
              </div>
            </div>
            <EngineIndicator status={status} />
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-5">

        <Instructions />

        {/* Layers */}
        <section className="bg-white rounded-2xl border border-gray-200 shadow-[0_2px_12px_rgba(0,53,148,0.06)] p-6">
          <SectionHeader title="Layers" count={layers.length} min={2} max={7} onChange={setLayerCount} />
          <LayersTable layers={layers} errors={layerErrors} units={units} onChange={l => { setLayers(l); clearResults(); }} />
          {hasErrors(layerErrors) && (
            <div className="mt-3 flex flex-col gap-0.5">
              {errorMessages(layerErrors).map(m => (
                <p key={m} style={monoFont} className="text-xs text-red-500">⚠ {m}</p>
              ))}
            </div>
          )}
        </section>

        {/* Loads */}
        <section className="bg-white rounded-2xl border border-gray-200 shadow-[0_2px_12px_rgba(0,53,148,0.06)] p-6">
          <SectionHeader title="Loads" count={loads.length} min={1} max={7} onChange={setLoadCount}
            rightSlot={
              <div className="flex items-center gap-2 ml-1">
                <label style={monoFont} className="text-xs text-gray-500">Define by</label>
                <select value={loadMode} onChange={e => setLoadMode(e.target.value as LoadInputMode)} className={selectCls}>
                  {LOAD_MODE_ORDER.map(m => <option key={m} value={m}>{LOAD_MODE_SPEC[m].label}</option>)}
                </select>
              </div>
            }
          />
          <LoadsTable loads={loads} units={units} mode={loadMode} onValidityChange={setLoadsInvalid} onChange={l => { setLoads(l); clearResults(); }} />
        </section>

        {/* Analysis mode toggle + config */}
        <section className="bg-white rounded-2xl border border-gray-200 shadow-[0_2px_12px_rgba(0,53,148,0.06)] p-6">

          {/* Toggle pill */}
          <div className="flex items-center gap-1 mb-6 bg-gray-100 rounded-xl p-1 w-fit">
            {(['plot','points'] as AnalysisMode[]).map(m => (
              <button key={m}
                onClick={() => { if (m !== analysisMode) clearResults(); setRunError(null); setAnalysisMode(m); }}
                className={`px-5 py-1.5 rounded-lg text-sm font-medium transition-all ${
                  analysisMode===m
                    ? 'bg-[#003594] text-white shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {m==='plot' ? 'Field Plot' : 'Individual Points'}
              </button>
            ))}
          </div>

          {analysisMode === 'plot' && (
            <>
              <h2 style={serifFont} className="text-xl font-semibold text-[#001f5b] mb-4">Analysis Grid</h2>
              <GridConfigPanel config={grid} units={units} onChange={c => { setGrid(c); setPlotResults(null); setRunError(null); }} />
            </>
          )}

          {analysisMode === 'points' && (
            <>
              <SectionHeader title="Analysis Points" count={points.length} min={1} max={20} onChange={setPointCount} />
              <PointsTable points={points} errors={pointErrors} layers={layers} units={units} onChange={p => { setPoints(p); setPointResults(null); setRunError(null); }} />
              {hasErrors(pointErrors) && (
                <div className="mt-3 flex flex-col gap-0.5">
                  {errorMessages(pointErrors).map(m => (
                    <p key={m} style={monoFont} className="text-xs text-red-500">⚠ {m}</p>
                  ))}
                </div>
              )}
            </>
          )}
        </section>

        {/* Analyze button */}
        <div className="flex items-center justify-between">
          <button
            onClick={analysisMode==='plot' ? handleRunPlot : handleRunPoints}
            disabled={!isReady || isRunning || !inputsValid}
            className="px-6 py-2.5 rounded-lg bg-[#003594] text-white font-semibold text-sm hover:bg-[#00276e] disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-[0_2px_12px_rgba(0,53,148,0.25)]"
          >
            {isRunning ? 'Running…' : 'Analyze'}
          </button>
          <span style={monoFont} className={`text-sm ${statusCls}`}>{statusText}</span>
        </div>

        {/* Field Plot results */}
        {analysisMode==='plot' && plotResults && plotResults.length > 0 && (
          <section className="bg-white rounded-2xl border border-gray-200 shadow-[0_2px_12px_rgba(0,53,148,0.06)] p-6">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <h2 style={serifFont} className="text-xl font-semibold text-[#001f5b]">Results</h2>
                <span style={monoFont} className="text-xs text-gray-500">
                  {formatPropertyName(property)} · {grid.plotType} plane
                </span>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <select value={property} onChange={e => setProperty(e.target.value as ResultKey)} className={selectCls}>
                  {PROPERTIES.filter(p => !p.group).map(p => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                  <optgroup label="── Principal Stresses ──">
                    {PROPERTIES.filter(p => p.group==='Principal Stresses').map(p => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </optgroup>
                </select>
                <button onClick={cycleMode}
                  className="rounded border border-[#003594]/30 px-3 py-1 text-xs bg-white text-[#003594] hover:bg-[#003594]/5 transition-colors font-medium">
                  {MODE_LABELS[plotMode]} (active)
                </button>
                <button
                  onClick={() => setColorScale(cs => cs === 'global' ? 'per-layer' : 'global')}
                  title={colorScale === 'global' ? 'Switch to per-layer color scale' : 'Switch to global color scale'}
                  className={`rounded border px-3 py-1 text-xs font-medium transition-colors flex items-center gap-1.5 ${
                    colorScale === 'per-layer'
                      ? 'border-[#FFB81C] bg-[#FFB81C]/10 text-[#7a5500]'
                      : 'border-gray-200 bg-white text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <svg viewBox="0 0 16 16" fill="currentColor" width="12" height="12">
                    <rect x="1" y="1" width="14" height="5" rx="1" opacity={colorScale === 'per-layer' ? '1' : '0.4'} />
                    <rect x="1" y="10" width="14" height="5" rx="1" opacity={colorScale === 'per-layer' ? '0.5' : '0.4'} />
                  </svg>
                  {colorScale === 'per-layer' ? 'Per-layer scale' : 'Global scale'}
                </button>
                <button onClick={downloadPlot}
                  title="Download plot as PNG"
                  className="rounded border border-gray-200 bg-white px-3 py-1 text-xs text-gray-500 hover:text-gray-700 hover:border-gray-300 transition-colors font-medium flex items-center gap-1.5">
                  <svg viewBox="0 0 16 16" fill="currentColor" width="12" height="12">
                    <path d="M8 1a.75.75 0 0 1 .75.75v6.44l1.97-1.97a.75.75 0 1 1 1.06 1.06l-3.25 3.25a.75.75 0 0 1-1.06 0L4.22 7.28a.75.75 0 0 1 1.06-1.06L7.25 8.19V1.75A.75.75 0 0 1 8 1zM2.5 13.25a.75.75 0 0 1 .75-.75h9.5a.75.75 0 0 1 0 1.5h-9.5a.75.75 0 0 1-.75-.75z"/>
                  </svg>
                  PNG
                </button>
              </div>
            </div>

            <div style={monoFont} className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
              <span className="inline-flex items-center gap-1.5 rounded-md bg-[#003594]/5 px-2.5 py-1 text-[#003594]">
                {formatPropertyName(property)} in
                <span className="font-semibold">{plotValueUnit}</span>
              </span>
              <span className="text-gray-400">Coordinates in {plotCoordUnit}</span>
            </div>

            <div className="w-full relative">
              <div ref={plotlyRef}>
                <div id={plotId} style={{ width:'100%', height:600 }} />
              </div>
              <div ref={canvasRef} style={{ display:'none', width:'100%' }} />
            </div>
          </section>
        )}

        {/* Individual Points results */}
        {analysisMode==='points' && pointResults && pointResults.length > 0 && (
          <section className="bg-white rounded-2xl border border-gray-200 shadow-[0_2px_12px_rgba(0,53,148,0.06)] p-6">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
              <h2 style={serifFont} className="text-2xl font-semibold text-[#001f5b]">Results</h2>
              <NotationToggle value={resultNotation} onChange={setResultNotation} />
            </div>
            <p style={monoFont} className="text-xs text-gray-500 mb-5">
              Sign convention: <span className="font-semibold text-[#003594]">positive = tension</span>, <span className="font-semibold text-[#003594]">negative = compression</span>.
            </p>
            <ResultsTable results={pointResults} units={units} notation={resultNotation} />
          </section>
        )}
        <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <p style={monoFont} className="text-[0.65rem] text-[#003594] uppercase tracking-widest mb-1">Developed by</p>
            <p className="text-xs text-gray-600 leading-relaxed">
              <a href="https://www.linkedin.com/in/jorgeolava/en" target="_blank" rel="noopener noreferrer"
                className="font-medium text-[#003594] hover:underline">Jorge Olavarria</a>
              {' & '}
              <a href="https://www.linkedin.com/in/lev-khazanovich-44133b5" target="_blank" rel="noopener noreferrer"
                className="font-medium text-[#003594] hover:underline">Lev Khazanovich</a>
              {' · University of Pittsburgh, Department of Civil & Environmental Engineering · 2026'}
            </p>
          </div>
        </div>

      </main>

      <Footer />
    </div>
  );
};

export default MnLayerPlotPage;